import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface IUseRequestTimer {
  timerPrefixText: string;
  requestText: string;
  durationMs?: number;
}

export const useRequestTimer = ({ timerPrefixText, requestText, durationMs }: IUseRequestTimer) => {
  const [endTime, setEndTime] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const remainingTime = useMemo(() => {
    if (!endTime) {
      return 0;
    }
    return Math.max(0, Math.ceil((endTime - now) / 1000));
  }, [endTime, now]);

  // Update time every second
  useEffect(() => {
    if (endTime) {
      intervalRef.current = setInterval(() => {
        setNow(Date.now());
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [endTime]);

  // Stop timer when time is up
  useEffect(() => {
    if (remainingTime <= 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [remainingTime]);

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const buttonText = useMemo(() => {
    return remainingTime > 0 ? timerPrefixText + ' ' + formatTime(remainingTime) : requestText;
  }, [remainingTime, requestText, timerPrefixText]);

  const startTimer = useCallback(() => {
    const duration = durationMs || 5 * 60 * 1000; // Default to 5 mins
    setEndTime(Date.now() + duration);
    setNow(Date.now());
  }, [durationMs]);

  const resetTimer = useCallback(() => {
    setEndTime(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return {
    remainingTime,
    buttonText,
    startTimer,
    resetTimer
  };
};
